// Audio Manager — Web Audio API sound synthesis
export class AudioManager {
  private ctx: AudioContext;
  private masterGain: GainNode;
  private sfxGain: GainNode;
  private ambientGain: GainNode;
  private _sfxVolume = 0.8;
  private _ambientVolume = 0.05;
  private _muted = false;

  constructor() {
    this.ctx = new AudioContext();
    this.masterGain = this.ctx.createGain();
    this.sfxGain = this.ctx.createGain();
    this.ambientGain = this.ctx.createGain();

    this.sfxGain.connect(this.masterGain);
    this.ambientGain.connect(this.masterGain);
    this.masterGain.connect(this.ctx.destination);

    this.sfxGain.gain.value = this._sfxVolume;
    this.ambientGain.gain.value = this._ambientVolume;
  }

  async init(): Promise<void> {
    if (this.ctx.state === 'suspended') {
      await this.ctx.resume();
    }
  }

  private playTone(
    type: OscillatorType,
    freq: number,
    duration: number,
    gainPeak: number,
    filterFreq?: number
  ): void {
    if (this._muted) return;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();

    osc.type = type;
    osc.frequency.value = freq;

    gain.gain.setValueAtTime(gainPeak, this.ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + duration);

    if (filterFreq) {
      const filter = this.ctx.createBiquadFilter();
      filter.type = 'bandpass';
      filter.frequency.value = filterFreq;
      filter.Q.value = 2;
      osc.connect(filter);
      filter.connect(gain);
    } else {
      osc.connect(gain);
    }

    gain.connect(this.sfxGain);
    osc.start(this.ctx.currentTime);
    osc.stop(this.ctx.currentTime + duration);
  }

  private playNoise(duration: number, gainPeak: number, filterFreq: number): void {
    if (this._muted) return;
    const bufferSize = this.ctx.sampleRate * duration;
    const buffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.exp(-i / (bufferSize * 0.1));
    }

    const noise = this.ctx.createBufferSource();
    noise.buffer = buffer;

    const filter = this.ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = filterFreq;
    filter.Q.value = 1.5;

    const gain = this.ctx.createGain();
    gain.gain.value = gainPeak;

    noise.connect(filter);
    filter.connect(gain);
    gain.connect(this.sfxGain);
    noise.start();
  }

  playSelect(): void {
    this.playTone('sine', 3000, 0.1, 0.3);
  }

  playLandStep(): void {
    this.playNoise(0.15, 0.4, 1000);
  }

  playLandHop(): void {
    this.playNoise(0.2, 0.5, 800);
    // Slight reverb tail via second quieter hit
    setTimeout(() => this.playNoise(0.1, 0.15, 600), 50);
  }

  playInvalid(): void {
    this.playTone('sine', 250, 0.1, 0.2, 200);
  }

  /** RULES §8.4 — rising chime per combo tier (2 / 3 / 4 / 5+). */
  playCombo(combo: number): void {
    if (this._muted) return;
    const base = combo >= 5 ? 1100 : combo === 4 ? 920 : combo === 3 ? 780 : 660;
    this.playTone('triangle', base, 0.16, 0.26);
    setTimeout(() => this.playTone('triangle', base * 1.5, 0.2, 0.22), 90);
  }

  playTurnChange(): void {
    this.playTone('triangle', 400, 0.1, 0.2, 300);
  }

  playWin(): void {
    if (this._muted) return;
    const notes = [523.25, 659.25, 783.99, 1046.5]; // C5, E5, G5, C6
    notes.forEach((freq, i) => {
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();

      osc.type = 'square';
      osc.frequency.value = freq;

      const start = this.ctx.currentTime + i * 0.18;
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(0.15, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, start + 0.5);

      osc.connect(gain);
      gain.connect(this.sfxGain);
      osc.start(start);
      osc.stop(start + 0.5);
    });
  }

  setSfxVolume(v: number): void {
    this._sfxVolume = Math.max(0, Math.min(1, v));
    this.sfxGain.gain.value = this._sfxVolume;
  }

  setAmbientVolume(v: number): void {
    this._ambientVolume = Math.max(0, Math.min(1, v));
    this.ambientGain.gain.value = this._ambientVolume;
  }

  setMuted(m: boolean): void {
    this._muted = m;
    this.masterGain.gain.value = m ? 0 : 1;
  }

  get sfxVolume(): number {
    return this._sfxVolume;
  }
  get ambientVolume(): number {
    return this._ambientVolume;
  }
  get muted(): boolean {
    return this._muted;
  }
}
